import { createPortal } from 'react-dom';
import {
  JUDGE_SHEET_MIN_ROWS,
  type JudgeSheetColumn,
} from '@/lib/judge-sheet-templates';

export interface JudgeSheetContestant {
  position: number;
  /** Contestant name; for team roping this is the header. */
  name: string;
  /**
   * Team-roping partner (the heeler). When present the contestant cell shows
   * both names — `name` as the header, `partnerName` as the heeler.
   */
  partnerName?: string | null;
}

/** One performance's worth of contestants, rendered as its own page. */
export interface JudgeSheetPerformance {
  /** Stable key (the performance id, or a synthetic bucket id). */
  id: string;
  /** Heading printed in the gray card for this performance's sheet. */
  name: string;
  /**
   * Pre-formatted date/time of this performance, shown in the header's
   * small print. Falls back to the event-level `eventDate` when omitted.
   */
  dateTime?: string;
  /** Contestants in the exact order they should print (caller pre-sorts). */
  contestants: JudgeSheetContestant[];
}

export interface JudgeSheetPrintableProps {
  organizationName: string;
  rodeoName: string;
  /** "1st Go Round", "Short Round", etc. */
  roundLabel: string;
  /** Discipline label printed in the gray card (e.g. "Barrel Racing"). */
  eventTypeName: string;
  /** Pre-formatted human date string, e.g. "Friday, October 3, 2025". */
  eventDate: string;
  /** Ordered column headers; the contestant column is rendered separately. */
  columns: JudgeSheetColumn[];
  /**
   * Flat contestant list — renders a single sheet. Used by the template
   * editor's live preview. Ignored when `performances` is supplied.
   */
  contestants?: JudgeSheetContestant[];
  /**
   * When provided, render one sheet per performance with a page break
   * between each, contestants printed in the given order. The flat
   * `contestants` prop is ignored in this mode.
   */
  performances?: JudgeSheetPerformance[];
  /** Override the default trailing blank-row count. */
  minRows?: number;
  /**
   * When true, skip the document.body portal — used by the admin editor's
   * live preview, which renders the sheet inline at scale-down. The print
   * path always uses the portal.
   */
  inline?: boolean;
}

/**
 * Print-only rendering of the rodeo Judge's Sheet. Targets US Letter portrait.
 *
 * Visual reference: docs/judges_sheets_examples/*.pdf
 *
 * Columns are passed in directly (DB-driven) so this component has no
 * coupling to any specific template catalog — same component renders both
 * the built-in templates and any custom one configured in Settings.
 *
 * Two layouts: a single flat sheet (template preview), or one sheet per
 * performance (the post-draw print). In the per-performance layout every
 * sheet carries the performance name as its heading, the contestants print
 * in the order the caller supplies, and a page break separates each sheet so
 * each performance lands on its own page. Standard printer margins come from
 * the `@page` rule in index.css.
 */
export function JudgeSheetPrintable({
  organizationName,
  rodeoName,
  roundLabel,
  eventTypeName,
  eventDate,
  columns,
  contestants,
  performances,
  minRows = JUDGE_SHEET_MIN_ROWS,
  inline = false,
}: JudgeSheetPrintableProps) {
  // Per-performance mode when a non-empty `performances` array is supplied;
  // otherwise fall back to a single flat sheet labelled with `roundLabel`.
  const sheetsData =
    performances && performances.length > 0
      ? performances.map((p) => ({
          key: p.id,
          label: p.name,
          dateTime: p.dateTime || eventDate,
          contestants: p.contestants,
        }))
      : [
          {
            key: 'single',
            label: roundLabel,
            dateTime: eventDate,
            contestants: contestants ?? [],
          },
        ];

  const renderSheet = (
    key: string,
    label: string,
    dateTimeLabel: string,
    sheetContestants: JudgeSheetContestant[],
  ) => {
    const totalRows = Math.max(minRows, sheetContestants.length);
    const rows = Array.from(
      { length: totalRows },
      (_, i) => sheetContestants[i] ?? null,
    );

    return (
      <div
        key={key}
        className="judge-sheet-print bg-white text-black mx-auto p-10"
        style={{
          width: '8.5in',
          minHeight: '11in',
          fontFamily: '"Inter", "Helvetica Neue", Arial, sans-serif',
        }}
      >
        {/* ── Header ── */}
        <div className="flex items-start justify-between gap-8 mb-8">
          <div className="flex-1">
            <h1 className="text-4xl font-light tracking-tight">Judge's Sheet</h1>
            <div className="mt-12 border-b border-black/80 pt-1">
              <span className="text-sm">Name:</span>
            </div>
          </div>
          <div className="w-[3.2in] bg-neutral-200/70 rounded-xl px-5 py-3">
            <div className="text-right text-[11px] text-neutral-700">{organizationName}</div>
            <div className="text-right text-lg font-medium leading-tight">
              {rodeoName} {label}
            </div>
            <div className="mt-2 bg-white rounded-md px-3 py-1.5 text-[13px] leading-tight">
              <div>{eventTypeName}</div>
              {/* Per-performance date/time (falls back to the event date for
                  buckets without a scheduled performance). */}
              <div className="text-neutral-700">{dateTimeLabel}</div>
            </div>
          </div>
        </div>

        {/* ── Table ── */}
        <table className="w-full border-collapse text-[12px]">
          <thead>
            <tr className="bg-[#5b1414] text-white">
              <th className="w-10 border border-[#5b1414] py-2 text-center font-medium tracking-wide">&nbsp;</th>
              <th className="border border-[#5b1414] py-2 px-3 text-left font-medium tracking-wide">CONTESTANT</th>
              {columns.map((col, i) => (
                <th key={i} className={`border border-[#5b1414] py-2 px-2 text-left font-medium tracking-wide ${col.widthClass ?? ''}`}>
                  {col.label}
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {rows.map((row, idx) => (
              <tr key={idx} className="h-9">
                <td className="border border-neutral-400 text-center text-neutral-600 tabular-nums">{idx + 1}</td>
                <td className="border border-neutral-400 px-3 italic">
                  {row?.partnerName ? (
                    /* Team roping — header on top, heeler below, kept tight so
                       the row height (and page count) is unchanged. */
                    <div className="leading-tight">
                      <div>
                        {row.name}
                        <span className="not-italic text-[8px] uppercase tracking-wide text-neutral-500 ml-1">Header</span>
                      </div>
                      <div>
                        {row.partnerName}
                        <span className="not-italic text-[8px] uppercase tracking-wide text-neutral-500 ml-1">Heeler</span>
                      </div>
                    </div>
                  ) : (
                    row?.name ?? ''
                  )}
                </td>
                {columns.map((_, ci) => (
                  <td key={ci} className="border border-neutral-400" />
                ))}
              </tr>
            ))}
          </tbody>
        </table>

        {/* ── Footer ── */}
        <div className="mt-10 flex items-end justify-between gap-6">
          <div className="flex-1 border-b border-black/80 pb-1">
            <span className="text-sm">Signature:</span>
          </div>
          <img
            src="/icons/HighCallRodeo-icon-red.png"
            alt="HighCall Rodeo Solutions"
            className="w-20 h-20 object-contain"
          />
        </div>
      </div>
    );
  };

  const sheets = sheetsData.map((s) =>
    renderSheet(s.key, s.label, s.dateTime, s.contestants),
  );

  if (inline) return <>{sheets}</>;
  if (typeof document === 'undefined') return null;
  return createPortal(
    <div className="judge-sheet-print-portal">{sheets}</div>,
    document.body
  );
}
